import React, { useState, useMemo, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import CardSales from '@/components/Admin/CardSales';
import styled from 'styled-components';
import CardSalesTop from '@/components/Admin/CardSalesTop';
import SectionTitle from '@/components/Admin/SectionTitle';
import Table from '@/components/Admin/Table';
import {
   fetchTransactions,
   getTotalSalesThisMonthAndLastMonth,
   getTotalSalesThisWeekAndLastWeek,
   getTotalSales,
   getTotalSalesTodayAndYesterday,
   TotalSalesResponse,
   TodaySalesYesterdaySalesResponse,
   TotalSalesThisWeekAndLastWeekResponse,
   TotalSalesThisMonthAndLastMonthResponse,
   TotalPayoutReceived,
   TotalPayoutAvailable,
} from '@/api/transaction';
import { useUIStore } from '@/store/useUIStore';
import { DropdownActionType } from '@/types/shared';
import { useQuery } from '@tanstack/react-query';
import { fetchStores } from '@/api/store';
import { getAvailableVouchers, getSoldVouchers } from '@/api/voucher';
import useAuthStore from '@/store/authStore';
import { useTeamStore } from '@/store/useTeamStore';
import { getPayoutAvailable, getPayoutReceived } from '@/api/payout';
import { colors } from '@/constants/colors';

interface Props {
   sample: string;
}

export type TableTransaction = {
   _id: string;
   updatedAt: string;
   createdAt: string;
   status: string;
   voucherCode: string;
   store: string;
   amount: number;
   platformFee: number;
   xenditFee: number;
   netAmount: number;
   vat: number;
   customer: string;
   smsStatus?: string;
   smsError?: string;
};

const Home = () => {
   const { refresh, handleShow } = useUIStore();
   const { user } = useAuthStore();
   const acting = useTeamStore((s) => s.acting);
   const {
      data: transactionResponse,
      isLoading,
      error,
   } = useQuery({
      queryKey: ['transaction', refresh],
      queryFn: fetchTransactions,
   });
   const transactionData =
      transactionResponse?.transactions.map(
         (transaction: TableTransaction) => ({
            _id: transaction._id,
            updatedAt: transaction.updatedAt,
            createdAt: transaction.createdAt,
            status: transaction.status,
            voucherCode: transaction.voucherCode,
            store: transaction.store,
            amount: transaction.amount,
            platformFee: transaction.platformFee,
            xenditFee: transaction.xenditFee,
            netAmount: transaction.netAmount,
            vat: transaction.vat,
            customer: transaction.customer,
            smsStatus: transaction.smsStatus,
            smsError: transaction.smsError,
         })
      ) || [];

   // console.log(`transactionData`, transactionData);

   // const { data: transactions, isLoading: isLoadingTransactions } =
   // useQuery<TransactionResponse>({
   //    queryKey: ['transactions'],
   //    queryFn: fetchTransactions,
   // });

   // const { data: stores, isLoading: isLoadingStores } = useQuery({
   //    queryKey: ['stores'],
   //    queryFn: fetchStores,
   // });

   const {
      data: totalPayoutReceived,
      isLoading: isLoadingTotalPayoutReceived,
   } = useQuery<TotalPayoutReceived>({
      queryKey: ['totalPayoutReceived'],
      queryFn: getPayoutReceived,
   });
   const {
      data: totalPayoutAvailable,
      isLoading: isLoadingTotalPayoutAvailable,
   } = useQuery<TotalPayoutAvailable>({
      queryKey: ['totalPayoutAvailable'],
      queryFn: getPayoutAvailable,
   });

   // console.log(
   //    `-----totalPayoutAvailable`,
   //    totalPayoutAvailable?.totalPayoutAvailable
   // );

   const { data: totalSales, isLoading: isLoadingTotalSales } =
      useQuery<TotalSalesResponse>({
         queryKey: ['totalSales'],
         queryFn: getTotalSales,
      });

   const { data: weekSales, isLoading: isLoadingWeek } = useQuery({
      queryKey: ['weekSales'],
      queryFn: getTotalSalesThisWeekAndLastWeek,
   });

   const { data: monthSales, isLoading: isLoadingMonth } = useQuery({
      queryKey: ['monthSales'],
      queryFn: getTotalSalesThisMonthAndLastMonth,
   });

   const { data: availableVouchers, isLoading: isLoadingAvailableVouchers } =
      useQuery({
         queryKey: ['availableVouchers'],
         queryFn: getAvailableVouchers,
      });

   const { data: soldVouchers, isLoading: isLoadingSoldVouchers } = useQuery({
      queryKey: ['soldVouchers'],
      queryFn: getSoldVouchers,
   });

   const {
      data: totalSalesTodayAndYesterday,
      isLoading: isLoadingTotalSalesTodayAndYesterday,
   } = useQuery<TodaySalesYesterdaySalesResponse>({
      queryKey: ['totalSalesTodayAndYesterday'],
      queryFn: getTotalSalesTodayAndYesterday,
      initialData: { today: 0, yesterday: 0, percentage: 0 },
   });

   const {
      data: totalSalesThisWeekAndLastWeek,
      isLoading: isLoadingTotalSalesThisWeekAndLastWeek,
   } = useQuery<TotalSalesThisWeekAndLastWeekResponse>({
      queryKey: ['totalSalesThisWeekAndLastWeek'],
      queryFn: getTotalSalesThisWeekAndLastWeek,
      initialData: { thisWeek: 0, lastWeek: 0, percentage: 0 },
   });

   const {
      data: totalSalesThisMonthAndLastMonth,
      isLoading: isLoadingTotalSalesThisMonthAndLastMonth,
   } = useQuery<TotalSalesThisMonthAndLastMonthResponse>({
      queryKey: ['totalSalesThisMonthAndLastMonth'],
      queryFn: getTotalSalesThisMonthAndLastMonth,
      initialData: { thisMonth: 0, lastMonth: 0, percentage: 0 },
   });

   // console.log(
   //    `---- totalSalesThisMonthAndLastMonth`,
   //    totalSalesThisMonthAndLastMonth
   // );

   const PER_PAGE = 10;
   const [page, setPage] = useState(1);

   const totalPages = Math.max(1, Math.ceil(transactionData.length / PER_PAGE));

   useEffect(() => {
      if (page > totalPages) setPage(1);
   }, [totalPages, page]);

   const paginatedTransactions = useMemo(
      () => transactionData.slice((page - 1) * PER_PAGE, page * PER_PAGE),
      [transactionData, page]
   );

   const transactionHeadData = [
      {
         type: 'title',
         value: 'Date',
      },
      {
         type: 'title',
         value: 'Status',
      },
      {
         type: 'title',
         value: 'Voucher Code',
      },
      {
         type: 'title',
         value: 'Store',
      },
      {
         type: 'title',
         value: 'Amount',
      },
      {
         type: 'title',
         value: 'Contact',
      },
      {
         type: 'title',
         value: 'SMS',
      },
      {
         type: 'title',
         value: 'Action',
      },
   ];

   const dropdownActions: DropdownActionType<TableTransaction>[] = [
      {
         label: 'View Details',
         onClick: (data: TableTransaction) => {
            handleShow({
               template: 'transaction-detail',
               title: `Transaction Details`,
               windowType: 'desktop',
               data: data,
            });
         },
         icon: 'file-copy-line',
      },
   ];

   return (
      <Wrapper>
         <GreetingsWrapper>
            <Greetings>Hello!, Welcome back,</Greetings>
            <Name>{acting ? acting.ownerName || acting.ownerEmail.split('@')[0] : user?.name || user?.email?.split('@')[0] || 'Guest'}</Name>
         </GreetingsWrapper>

         <CardsContent>
            <CardSalesTop
               totalSalesTodayAndYesterday={totalSalesTodayAndYesterday}
               totalSalesThisWeekAndLastWeek={totalSalesThisWeekAndLastWeek}
               totalSalesThisMonthAndLastMonth={totalSalesThisMonthAndLastMonth}
            />
            <CardSales
               totalPayoutReceived={
                  totalPayoutReceived?.totalPayoutReceived || 0
               }
               totalPayoutAvailable={
                  totalPayoutAvailable?.totalPayoutAvailable || 0
               }
               soldVouchers={soldVouchers?.result || 0}
               availableVouchers={availableVouchers?.result || 0}
               totalSales={totalSales?.totalSales || 0}
            />
         </CardsContent>

         <ContentItem>
            <SectionTitle title="Transaction History" />
            <Table<TableTransaction>
               headData={transactionHeadData}
               data={paginatedTransactions}
               grid={`110px 1fr 2fr 1fr 1fr 1fr 90px auto`}
               type="transaction"
               dropdownData={dropdownActions}
               onRowClick={(data) => {
                  handleShow({
                     template: 'transaction-detail',
                     title: 'Transaction Details',
                     windowType: 'desktop',
                     data: data,
                  });
               }}
            />

            {totalPages > 1 && (
               <Pagination>
                  <PageBtn
                     disabled={page === 1}
                     onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                     Prev
                  </PageBtn>
                  <PageNumbers>
                     {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => {
                           if (totalPages <= 7) return true;
                           if (p === 1 || p === totalPages) return true;
                           if (Math.abs(p - page) <= 1) return true;
                           return false;
                        })
                        .reduce<(number | string)[]>((acc, p, idx, arr) => {
                           if (idx > 0 && p - (arr[idx - 1] as number) > 1) {
                              acc.push('...');
                           }
                           acc.push(p);
                           return acc;
                        }, [])
                        .map((p, idx) =>
                           typeof p === 'string' ? (
                              <Ellipsis key={`e-${idx}`}>...</Ellipsis>
                           ) : (
                              <PageNum
                                 key={p}
                                 $active={p === page}
                                 onClick={() => setPage(p)}
                              >
                                 {p}
                              </PageNum>
                           )
                        )}
                  </PageNumbers>
                  <PageBtn
                     disabled={page === totalPages}
                     onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                     Next
                  </PageBtn>
               </Pagination>
            )}
         </ContentItem>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   /* font-family: 'Inter', sans-serif; */
`;

const GreetingsWrapper = styled.div`
   margin-top: 2rem;
   @media (max-width: 768px) {
      margin-top: 12px;
      padding: 0 12px;
   }
`;

const Greetings = styled.p`
   font-size: 18px;
   @media (max-width: 768px) {
      font-size: 13px;
      color: ${colors.GRAY5};
   }
`;

const Name = styled.p`
   font-size: 25px;
   font-weight: 700;
   @media (max-width: 768px) {
      font-size: 20px;
   }
`;

const CardsContent = styled.div`
   margin-top: 1rem;
   @media (max-width: 768px) {
      margin-top: 12px;
   }
`;

const ContentItem = styled.div`
   margin-top: 1rem;
   @media (max-width: 768px) {
      margin-top: 16px;
   }
`;

const Pagination = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   padding: 16px 1rem;
`;

const PageBtn = styled.button<{ disabled?: boolean }>`
   display: flex;
   align-items: center;
   gap: 4px;
   background: none;
   border: 1px solid
      ${({ disabled }) => (disabled ? colors.GRAY12 : colors.GRAY10)};
   border-radius: 4px;
   padding: 6px 12px;
   font-size: 13px;
   color: ${({ disabled }) => (disabled ? colors.GRAY10 : colors.GRAY1)};
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

   &:hover:not(:disabled) {
      background: ${colors.GRAY14};
   }
`;

const PageNumbers = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
`;

const PageNum = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'none')};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY1)};
   border: 1px solid
      ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 4px;
   width: 32px;
   height: 32px;
   font-size: 13px;
   cursor: pointer;

   &:hover {
      background: ${({ $active }) =>
         $active ? colors.PRIMARY : colors.GRAY14};
   }
`;

const Ellipsis = styled.span`
   color: ${colors.GRAY5};
   font-size: 13px;
   padding: 0 4px;
`;

export default Home;
